import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  CATALOG_VISIBLE_IMAGE_COUNT,
  IMAGE_ROTATION_INTERVAL_MS,
  IMAGE_TRANSITION_DURATION_MS,
} from '../../constants/catalogImageRotation';

const createCatalogRotationStore = () => {
  let state = {
    currentImageIndex: 0,
    rotationTick: 0,
  };
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot() {
      return state;
    },
    setCurrentImageIndex(nextIndex) {
      const normalizedIndex =
        ((nextIndex % CATALOG_VISIBLE_IMAGE_COUNT) + CATALOG_VISIBLE_IMAGE_COUNT) %
        CATALOG_VISIBLE_IMAGE_COUNT;

      if (state.currentImageIndex === normalizedIndex) {
        return;
      }

      state = {
        ...state,
        currentImageIndex: normalizedIndex,
      };
      notify();
    },
    advance({ rotateCatalogImages, rotateAttentionLoop }) {
      const nextState = {
        currentImageIndex: rotateCatalogImages
          ? (state.currentImageIndex + 1) % CATALOG_VISIBLE_IMAGE_COUNT
          : state.currentImageIndex,
        rotationTick: rotateAttentionLoop ? state.rotationTick + 1 : state.rotationTick,
      };

      if (
        nextState.currentImageIndex === state.currentImageIndex &&
        nextState.rotationTick === state.rotationTick
      ) {
        return;
      }

      state = nextState;
      notify();
    },
    reset() {
      if (state.currentImageIndex === 0 && state.rotationTick === 0) {
        return;
      }

      state = {
        currentImageIndex: 0,
        rotationTick: 0,
      };
      notify();
    },
  };
};

const catalogRotationStore = createCatalogRotationStore();
const CatalogImageRotationContext = createContext(catalogRotationStore);

export const CatalogImageRotationProvider = ({
  hasRotatingCatalogImages = false,
  hasAttentionLoop = false,
  prefersReducedMotion = false,
  children,
}) => {
  const store = useMemo(() => catalogRotationStore, []);

  useEffect(() => {
    if (prefersReducedMotion || (!hasRotatingCatalogImages && !hasAttentionLoop)) {
      store.reset();
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      store.advance({
        rotateCatalogImages: hasRotatingCatalogImages,
        rotateAttentionLoop: hasAttentionLoop,
      });
    }, IMAGE_ROTATION_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [hasAttentionLoop, hasRotatingCatalogImages, prefersReducedMotion, store]);

  return (
    <CatalogImageRotationContext.Provider value={store}>
      {children}
    </CatalogImageRotationContext.Provider>
  );
};

export const useCatalogImageRotation = () => {
  const store = useContext(CatalogImageRotationContext) || catalogRotationStore;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  return {
    ...snapshot,
    setCurrentImageIndex: store.setCurrentImageIndex,
    stepCount: CATALOG_VISIBLE_IMAGE_COUNT,
    transitionDurationMs: IMAGE_TRANSITION_DURATION_MS,
  };
};

export default CatalogImageRotationContext;
